import { lazy, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route, RouterProvider, useLocation, useParams } from 'react-router-dom';

import { PageFrame, RouteError, WorkspaceFrame } from '@/app/_frame/boundary';
import { PublicOnly, RequireOperator, RequirePermission } from '@/app/_frame/guards';
import { AuthFrame, Layout } from '@/app/_frame/layout';
import { NotFound } from '@/app/_frame/states';
import { Loading, ToastProvider } from '@/ui';
import { ACCOUNT_LINKS, DESTINATIONS, MERGED_TABS, mergedTabPath, WORKSPACE_STRIP, type AccountLink, type Destination, type MergedTab, type WorkspacePage } from '@/lib/nav';
import { OperatorProvider } from '@/lib/operator';
import type { PlatformPermission } from '@/lib/platform';

/**
 * The platform operator panel: one surface, one session, toolbar navigation.
 *
 *     /sign-in                      public
 *     /                             dashboard           PLATFORM_TENANT_READ
 *     /tenants                      list                PLATFORM_TENANT_READ
 *     /tenants/new                  create wizard       PLATFORM_TENANT_CREATE
 *     /tenants/:id                  → /tenants/:id/overview
 *     /tenants/:id/<tab>            workspace + tab     see WORKSPACE_STRIP
 *     /tenants/:id/email-templates  → /tenants/:id/mail?section=templates      (merged; ?template= kept)
 *     /tenants/:id/activity         → /tenants/:id/overview?section=activity   (merged; ?weeks= kept)
 *     /permissions                  catalogue           PLATFORM_TENANT_READ
 *     /models                       catalogue           PLATFORM_TENANT_READ
 *     /audit                        operator trail      PLATFORM_AUDIT_READ
 *     /defaults                     platform defaults   PLATFORM_TENANT_READ (edit: PLATFORM_OPERATOR_MANAGE) — account menu
 *     /__ui                         gallery             development builds only, no sign-in (fixtures, no API)
 *     *                             404
 *
 * Toolbar destinations and workspace tabs are gated by the same tables
 * (`lib/nav.ts`) that the shell draws from, so a link that is shown is a route
 * that is permitted, and the reverse.
 *
 * `ToastProvider` sits above every route, so `useToast()` works on every page
 * and a toast survives the navigation that a successful save often triggers.
 *
 * Every page is its own chunk (`lazy`), inside its own error boundary and
 * Suspense (`PageFrame`), so one page that throws, or one chunk that fails to
 * load after a deploy, takes down that page and not the toolbar.
 *
 * A data router (`createBrowserRouter`), not `<BrowserRouter>`: it is what
 * makes `useBlocker` available, so a page with unsaved edits can hold the
 * browser's Back and Forward as well as a link (`lib/leave-guard.ts`). The
 * route table is the same JSX, through `createRoutesFromElements`; no route
 * uses a loader or an action — data is still read by the pages.
 */

/** `React.lazy` over a named export — pages export their component by name, not as default. */
function lazyPage<M, K extends keyof M>(load: () => Promise<M>, name: K): ComponentType {
  return lazy(async () => ({ default: (await load())[name] as ComponentType }));
}

const SignInPage = lazyPage(() => import('@/app/sign-in'), 'SignInPage');
const DashboardPage = lazyPage(() => import('@/app/dashboard'), 'DashboardPage');
const TenantsPage = lazyPage(() => import('@/app/tenants'), 'TenantsPage');
const TenantNewPage = lazyPage(() => import('@/app/tenant-new'), 'TenantNewPage');
const TenantWorkspacePage = lazyPage(() => import('@/app/tenant'), 'TenantWorkspacePage');
const PermissionsPage = lazyPage(() => import('@/app/permissions'), 'PermissionsPage');
const ModelsPage = lazyPage(() => import('@/app/models'), 'ModelsPage');
const AuditPage = lazyPage(() => import('@/app/audit'), 'AuditPage');
const DefaultsPage = lazyPage(() => import('@/app/defaults'), 'DefaultsPage');
/** Declared only in development, so the production bundle has no gallery chunk at all — not merely no route to it. */
const UiGalleryPage = import.meta.env.DEV ? lazyPage(() => import('@/app/ui-gallery'), 'UiGalleryPage') : null;

/** One entry per page in the context bar, keyed by the same union `lib/nav.ts` routes and draws. */
const TAB_PAGES: Record<WorkspacePage, ComponentType> = {
  overview: lazyPage(() => import('@/app/tenant/overview'), 'TenantOverviewTab'),
  content: lazyPage(() => import('@/app/tenant/content'), 'TenantContentTab'),
  members: lazyPage(() => import('@/app/tenant/members'), 'TenantMembersTab'),
  roles: lazyPage(() => import('@/app/tenant/roles'), 'TenantRolesTab'),
  mail: lazyPage(() => import('@/app/tenant/mail'), 'TenantMailTab'),
  microsoft: lazyPage(() => import('@/app/tenant/microsoft'), 'TenantMicrosoftTab'),
  models: lazyPage(() => import('@/app/tenant/models'), 'TenantModelsTab'),
  audit: lazyPage(() => import('@/app/tenant/audit'), 'TenantAuditTab'),
};

/**
 * A merged tab's old URL → its section in the page it joined, with the rest of
 * the query kept. `replace`, so Back does not bounce through the old address.
 * No gate of its own: the page it lands on is gated.
 */
function MergedTabRedirect({ tab }: { tab: MergedTab }) {
  const { id = '' } = useParams();
  const { search, hash } = useLocation();

  return <Navigate to={`${mergedTabPath(id, tab, search)}${hash}`} replace />;
}

const TOOLBAR_PAGES: Record<Destination['key'], ComponentType> = {
  dashboard: DashboardPage,
  tenants: TenantsPage,
  permissions: PermissionsPage,
  models: ModelsPage,
  audit: AuditPage,
};

const ACCOUNT_PAGES: Record<AccountLink['key'], ComponentType> = {
  defaults: DefaultsPage,
};

function gated(permission: PlatformPermission, Page: ComponentType): ReactNode {
  return (
    <RequirePermission permission={permission}>
      <PageFrame>
        <Page />
      </PageFrame>
    </RequirePermission>
  );
}

/** Above every route: the session and the toasts, both of which navigate or outlive a navigation. */
function Root() {
  return (
    <OperatorProvider>
      <ToastProvider>
        <Outlet />
      </ToastProvider>
    </OperatorProvider>
  );
}

/** Built once, at module load: a data router is created outside React and handed to `RouterProvider`. */
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<Root />} errorElement={<RouteError />}>
      <Route
        path="/sign-in"
        element={
          <PublicOnly>
            <AuthFrame>
              <PageFrame fallback={<Loading />}>
                <SignInPage />
              </PageFrame>
            </AuthFrame>
          </PublicOnly>
        }
      />

      {UiGalleryPage === null ? null : (
        <Route
          path="/__ui"
          element={
            <PageFrame>
              <UiGalleryPage />
            </PageFrame>
          }
        />
      )}

      <Route
        element={
          <RequireOperator>
            <Layout />
          </RequireOperator>
        }
      >
        {DESTINATIONS.map((destination) =>
          destination.key === 'dashboard' ? (
            <Route key={destination.key} index element={gated(destination.permission, TOOLBAR_PAGES.dashboard)} />
          ) : (
            <Route
              key={destination.key}
              path={destination.to.slice(1)}
              element={gated(destination.permission, TOOLBAR_PAGES[destination.key])}
            />
          ),
        )}

        {ACCOUNT_LINKS.map((link) => (
          <Route key={link.key} path={link.to.slice(1)} element={gated(link.permission, ACCOUNT_PAGES[link.key])} />
        ))}

        {/* Static beats dynamic in React Router's ranking, so `new` is never read as an id. */}
        <Route path="tenants/new" element={gated('PLATFORM_TENANT_CREATE', TenantNewPage)} />

        <Route
          path="tenants/:id"
          element={
            <RequirePermission permission="PLATFORM_TENANT_READ">
              <WorkspaceFrame>
                <TenantWorkspacePage />
              </WorkspaceFrame>
            </RequirePermission>
          }
        >
          <Route index element={<Navigate to="overview" replace />} />
          {WORKSPACE_STRIP.map(({ tab, permission }) => (
            <Route key={tab} path={tab} element={gated(permission, TAB_PAGES[tab])} />
          ))}
          {(Object.keys(MERGED_TABS) as MergedTab[]).map((tab) => (
            <Route key={tab} path={tab} element={<MergedTabRedirect tab={tab} />} />
          ))}
          <Route path="*" element={<NotFound />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Route>,
  ),
);

export function App() {
  return <RouterProvider router={router} />;
}
